import React, {useMemo} from 'react';
import {Box, Button, Grid, Typography} from '@mui/material';
import {gql, useQuery} from '@apollo/client';
import {capitalize} from 'lodash-es';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import {
  AccountSearchInput,
  useAccountSearch,
} from '../../components/AccountSearchInput/AccountSearchInput';
import {useAutoField, useAutoForm} from '../../components/AutoForm/useAutoForm';
import AutoField, {
  ActionType,
  SelectField,
  SelectOption,
} from '../../components/AutoForm/AutoFieldNew';
import {ContactEmails, ContactEmailsVariables} from '../../schema-types/ContactEmails';
import {EmailAuditItem} from '../../schema-types/EmailAuditItem';
import Loading from '../../components/Loading/Loading';
import {SearchAccounts_accounts_contacts} from '../../schema-types/SearchAccounts';
import {newUTCDate} from '../../utils/newUTCDate';
import {EmailAuditStatusEnum} from '../../schema-types/globalTypes';
import {AccountBasicFragment} from '../../schema-types/AccountBasicFragment';
import {UPDATES} from '../../shared/UPDATES';

type emailAuditFormTypes = {
  email: string;
  accountId: string;
  contactId: string;
  page: number;
};

const paginationCount = 20;

type EmailAuditProps = {forcedAccount?: AccountBasicFragment};

export const EmailAuditInner: React.FC<EmailAuditProps> = ({forcedAccount}) => {
  const {values} = useAutoForm<emailAuditFormTypes>();

  const {account: _account, searchField, searchLoading} = useAccountSearch('accountId');
  const account = useMemo(() => {
    if (forcedAccount) {
      return forcedAccount;
    }
    return _account;
  }, [_account, forcedAccount]);
  const contact = useMemo(() => {
    if (!account) {
      return null;
    }
    const contactFound = account.contacts?.find((x) => x.id === values.contactId);
    if (contactFound) {
      return contactFound;
    }
    return null;
  }, [account, values.contactId]);

  const contactField: SelectField = useMemo(() => {
    const options: SelectOption[] = [];
    account?.contacts?.forEach((c) => {
      options.push({label: `${c.simplifiedName} (${c.email})`, value: c.id});
    });
    const field: SelectField = {
      id: 'contactId',
      label: 'Select Contact',
      options,
      type: ActionType.select,
    };
    return field;
  }, [account]);

  return (
    <>
      <Box p={2} bgcolor="white" borderRadius="3px">
        <Grid container spacing={3}>
          {forcedAccount ? null : (
            <Grid item xs={6}>
              <AccountSearchInput
                account={account}
                searchField={searchField}
                searchLoading={searchLoading}
              />
            </Grid>
          )}
          <Grid item xs={6}>
            <AutoField field={contactField} />
          </Grid>
        </Grid>
      </Box>
      <Box mt={3}>
        <EmailDisplay contact={contact} />
      </Box>
    </>
  );
};

const EmailDisplay: React.FC<{
  contact: SearchAccounts_accounts_contacts | null | undefined;
}> = ({contact}) => {
  const {values} = useAutoForm<emailAuditFormTypes>();
  const [{value: page}, _, {setValue: setPage}] =
    useAutoField<emailAuditFormTypes['page']>('page');
  const {data, loading} = useQuery<ContactEmails, ContactEmailsVariables>(
    gql`
      fragment EmailAuditItem on EmailAuditItem {
        senderAddress
        destinationAddress
        subject
        dateSent
        status
        statusDescription
      }
      query ContactEmails($contactId: UUID!, $first: Int, $after: String) {
        contactEmails(contactId: $contactId, first: $first, after: $after) {
          edges {
            node {
              ...EmailAuditItem
            }
            cursor
          }
          pageInfo {
            hasNextPage
            startCursor
            endCursor
          }
        }
      }
    `,
    {
      variables: {
        contactId: contact?.id,
        first: paginationCount * page,
      },
      skip: !contact?.id,
    }
  );
  if (!contact) {
    return null;
  }
  if (loading) {
    return <Loading />;
  }
  if (!data?.contactEmails?.edges.length) {
    return (
      <Box>
        No emails found for {contact.simplifiedName} ({contact.email})
      </Box>
    );
  }
  return (
    <>
      {data?.contactEmails?.edges.map((edge) => {
        return <EmailBox key={edge?.cursor} item={edge!.node!} />;
      })}
      <Box display="flex" justifyContent="center">
        {data?.contactEmails?.pageInfo.hasNextPage ? (
          <Button
            onClick={() => {
              setPage(UPDATES._4600, values.page + 1);
            }}
          >
            View more
          </Button>
        ) : (
          <Typography>Viewing all emails</Typography>
        )}
      </Box>
    </>
  );
};

const EmailBox: React.FC<{item: EmailAuditItem}> = ({item}) => {
  return (
    <Box mb={2} p={2} bgcolor="white" borderRadius="3px">
      <Grid container spacing={3}>
        <Grid item xs={4}>
          {newUTCDate(item.dateSent).toDateString()}{' '}
          {newUTCDate(item.dateSent).toLocaleTimeString()}
          {/* <br />
          Subject: */}
          <br />
          <b>{item.subject}</b>
        </Grid>
        <Grid item xs={4}>
          To: {item.destinationAddress}
          <br />
          From: {item.senderAddress}
        </Grid>

        <Grid item xs={4}>
          Status:{' '}
          <Typography
            display="inline"
            color={item.status === EmailAuditStatusEnum.received ? 'green' : 'red'}
          >
            <b>{capitalize(item.status || 'Unknown')}</b>
          </Typography>
          <br />
          <b>{item.statusDescription}</b>
        </Grid>
      </Grid>
    </Box>
  );
};

export const EmailAuditWrapped = withAutoFormNew<emailAuditFormTypes, EmailAuditProps>(
  EmailAuditInner,
  undefined,
  {debugPosition: 'top'}
);

export const EmailAuditPage: React.FC = () => {
  return (
    <>
      <Grid mt={3} container direction="column">
        <Grid item>
          <Typography variant="h4">Email audit tool</Typography>
        </Grid>
        <EmailAuditWrapped formFields={[]} initialValueOverrides={{page: 1}} />;
      </Grid>
    </>
  );
};
